import { Alert, Button, Fieldset } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { PhotoAttachmentsField } from 'src/Components/Forms/PhotoAttachmentsField';
import { InventoryInvoices } from 'src/Components/Inventory/InventoryInvoices';
import { InventoryItemFields } from 'src/Components/Inventory/InventoryItemFields';
import { INVENTORY_PHOTOS_MAX } from 'src/Components/Inventory/inventoryLabels';

import styles from './InventoryItemForm.module.scss';
import { Props } from './InventoryItemForm.types';

/** Folder of Documents where the photos taken from the inventory are filed. */
const PHOTOS_FOLDER = '/Photos de l’inventaire/';

/** One screen for an object, laid out like the other drawer forms (contact, expense). */
const InventoryItemForm = ({
  values,
  onChange,
  photos,
  onPhotosChange,
  onPhotosUploadingChange,
  invoices,
  onInvoicesChange,
  roomOptions,
  valuesVisible,
  rule,
  sources,
  errors,
  lucienSection,
  suggestionsSection,
  invoiceSuggestions,
  onSubmit,
  onCancel,
  isLoading,
  submitLabel,
  submitAndAddAnotherLabel,
  onSubmitAndAddAnother,
}: Props) => {
  const { t } = useTranslation();

  return (
    <form
      className={styles.container}
      onSubmit={event => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <Fieldset legend={t('inventory.photos.title', 'Photos')}>
        <PhotoAttachmentsField
          value={photos}
          onChange={onPhotosChange}
          onUploadingChange={onPhotosUploadingChange}
          max={INVENTORY_PHOTOS_MAX}
          uploadFolder={PHOTOS_FOLDER}
          sortable
          firstBadge={t('inventory.photos.main', 'Photo principale')}
          label={t('inventory.photos.add', 'Ajouter des photos')}
          modalTitle={t('inventory.photos.modalTitle', 'Ajouter des photos de l’objet')}
          fileUploadLabel={t('inventory.photos.uploadLabel', 'Importer des photos')}
          existingFileLabel={t('inventory.photos.existingLabel', 'Lier une photo de Documents')}
          error={errors.photos}
        />
      </Fieldset>

      {lucienSection}

      <InventoryItemFields
        values={values}
        onChange={onChange}
        roomOptions={roomOptions}
        valuesVisible={valuesVisible}
        rule={rule}
        sources={sources}
        nameError={errors.name}
      />

      {suggestionsSection}

      <Fieldset legend={t('inventory.invoices.title', 'Factures')}>
        <div className={styles.section}>
          {invoiceSuggestions}
          <InventoryInvoices
            invoices={invoices}
            onAdd={invoice => onInvoicesChange(current => [...current, invoice])}
            onRemove={id => onInvoicesChange(current => current.filter(i => i.id !== id))}
          />
        </div>
      </Fieldset>

      {errors.submit && <Alert color="red">{errors.submit}</Alert>}

      <div className={styles.border} />
      <div className={styles.buttons}>
        <Button onClick={onCancel} variant="outline" className="btn-medium">
          {t('common.cancel', 'Annuler')}
        </Button>
        <div className={styles.saveButtons}>
          {submitAndAddAnotherLabel && onSubmitAndAddAnother && (
            <Button variant="light" loading={isLoading} onClick={onSubmitAndAddAnother}>
              {submitAndAddAnotherLabel}
            </Button>
          )}
          <Button variant="primary" type="submit" loading={isLoading}>
            {submitLabel}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default InventoryItemForm;
